import { useState, useEffect } from "react";
import { Search, Download, Loader2, ExternalLink, CheckCircle, AlertCircle, Globe, Plus, Clipboard, Sparkles, Phone, MapPin, Home, BarChart3, Play, Zap } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==================== OLX API SEARCH COMPONENT ====================
const OLXApiSearch = () => {
  const [query, setQuery] = useState("apartament");
  const [city, setCity] = useState("all");
  const [maxResults, setMaxResults] = useState(20);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [stats, setStats] = useState(null);
  const [runningCrawler, setRunningCrawler] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const [fetchingPhone, setFetchingPhone] = useState(null);
  
  const cities = [
    { value: "all", label: "Toate orașele" },
    { value: "bucuresti", label: "București" },
    { value: "cluj-napoca", label: "Cluj-Napoca" },
    { value: "timisoara", label: "Timișoara" },
    { value: "iasi", label: "Iași" },
    { value: "constanta", label: "Constanța" },
    { value: "brasov", label: "Brașov" },
    { value: "craiova", label: "Craiova" },
    { value: "galati", label: "Galați" },
    { value: "ploiesti", label: "Ploiești" },
    { value: "oradea", label: "Oradea" },
  ];
  
  const propertyTypes = [
    { value: "apartament", label: "Apartamente" },
    { value: "casa", label: "Case" },
    { value: "garsoniera", label: "Garsoniere" },
    { value: "teren", label: "Terenuri" },
    { value: "apartament vanzare", label: "Apartamente de vânzare" },
    { value: "casa vanzare", label: "Case de vânzare" },
  ];
  
  // Fetch crawler stats on mount
  useEffect(() => {
    fetchStats();
  }, []);
  
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/crawler/api/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data.stats);
    } catch (error) {
      // Stats might fail for non-admins, that's OK
      console.log("Stats not available:", error.response?.status);
    }
  };
  
  const handleSearch = async () => {
    if (!query.trim()) {
      toast.error("Introdu un termen de căutare");
      return;
    }
    
    setSearching(true);
    setResults([]);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/crawler/api/search`, {
        query: query.trim() + (city !== "all" ? ` ${city}` : ""),
        city: city !== "all" ? city : null,
        max_results: maxResults,
        fetch_phones: false
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      setResults(response.data.results);
      
      if (response.data.count === 0) {
        toast.info("Nu am găsit rezultate pentru această căutare");
      } else {
        toast.success(`${response.data.count} anunțuri găsite de pe OLX!`);
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error(error.response?.data?.detail || "Eroare la căutare");
    } finally {
      setSearching(false);
    }
  };
  
  const handleRunCrawler = async () => {
    setRunningCrawler(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/crawler/api/run`, {
        query: query.trim() + (city !== "all" ? ` ${city}` : ""),
        city: city !== "all" ? city : null,
        max_results: maxResults,
        fetch_phones: false
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      fetchStats(); // Refresh stats
    } catch (error) {
      console.error("Crawler error:", error);
      toast.error(error.response?.data?.detail || "Eroare la rularea crawler-ului");
    } finally {
      setRunningCrawler(false);
    }
  };
  
  const handleImport = async (externalId) => {
    setImportingId(externalId);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/crawler/api/import/${externalId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      
      // Remove from results
      setResults(prev => prev.filter(r => r.external_id !== externalId));
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.detail || "Eroare la import");
    } finally {
      setImportingId(null);
    }
  };
  
  const handleFetchPhone = async (externalId) => {
    setFetchingPhone(externalId);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/crawler/api/phone/${externalId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        // Update result with phone
        setResults(prev => prev.map(r => 
          r.external_id === externalId 
            ? { ...r, phone: response.data.phone }
            : r
        ));
        toast.success(`Telefon: ${response.data.phone}`);
      } else {
        toast.error("Nu am putut obține numărul de telefon");
      }
    } catch (error) {
      console.error("Phone fetch error:", error);
      toast.error(error.response?.data?.detail || "Eroare la obținerea telefonului");
    } finally {
      setFetchingPhone(null);
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Success Banner */}
      <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-emerald-800">
            <p className="font-semibold mb-1">✅ Căutare OLX Funcțională</p>
            <p>Această metodă folosește API-ul intern OLX și funcționează fără blocări. 
               Poți căuta anunțuri, vedea detalii și importa direct în platformă.</p>
          </div>
        </div>
      </div>
      
      {/* Stats Card (for admins) */}
      {stats && (
        <Card className="bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Statistici Crawler
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-violet-700">{stats.total_listings}</p>
                <p className="text-xs text-stone-500">Total anunțuri</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-600">{stats.unclaimed}</p>
                <p className="text-xs text-stone-500">Nerevendicate</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-emerald-600">{stats.claimed}</p>
                <p className="text-xs text-stone-500">Importate</p>
              </div>
            </div>
            {stats.last_run && (
              <p className="text-xs text-stone-500 mt-2 text-center">
                Ultima rulare: {new Date(stats.last_run).toLocaleString('ro-RO')}
              </p>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Search Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Caută anunțuri pe OLX</CardTitle>
          <CardDescription>Folosește API-ul OLX pentru a găsi anunțuri imobiliare în timp real</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Tip proprietate</Label>
              <Select value={query} onValueChange={setQuery}>
                <SelectTrigger data-testid="property-type-select">
                  <SelectValue placeholder="Alege tipul" />
                </SelectTrigger>
                <SelectContent>
                  {propertyTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Oraș</Label>
              <Select value={city} onValueChange={setCity}>
                <SelectTrigger data-testid="city-select">
                  <SelectValue placeholder="Toate orașele" />
                </SelectTrigger>
                <SelectContent>
                  {cities.map(c => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Rezultate maxime</Label>
              <Select value={String(maxResults)} onValueChange={(v) => setMaxResults(parseInt(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex gap-2">
          <Button 
            data-testid="search-olx-api-btn"
            onClick={handleSearch} 
            disabled={searching}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
          >
            {searching ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Se caută...
              </>
            ) : (
              <>
                <Search className="w-4 h-4 mr-2" />
                Caută pe OLX
              </>
            )}
          </Button>
          
          <Button 
            data-testid="run-crawler-btn"
            onClick={handleRunCrawler} 
            disabled={runningCrawler}
            variant="outline"
            className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
          >
            {runningCrawler ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Rulare...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Rulează & Salvează
              </>
            )}
          </Button>
        </CardFooter>
      </Card>
      
      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-stone-700">
              {results.length} anunțuri găsite
            </h3>
            <Badge variant="outline" className="text-blue-600 border-blue-600">
              OLX API
            </Badge>
          </div>
          
          <div className="grid gap-3">
            {results.map((result) => (
              <Card key={result.external_id} className="overflow-hidden hover:shadow-md transition-shadow">
                <div className="flex">
                  {/* Thumbnail */}
                  <div className="w-24 h-24 sm:w-32 sm:h-32 flex-shrink-0 bg-stone-100">
                    {result.images?.[0] ? (
                      <img 
                        src={result.images[0]} 
                        alt={result.title}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Home className="w-8 h-8 text-stone-300" />
                      </div>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 p-3 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-medium text-sm line-clamp-2">{result.title}</h4>
                      <p className="text-emerald-600 font-bold whitespace-nowrap">
                        {result.price?.toLocaleString()} {result.currency}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-3 mt-1 text-xs text-stone-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {result.city}{result.district ? `, ${result.district}` : ''}
                      </span>
                      {result.area && (
                        <span>{result.area} m²</span>
                      )}
                      {result.rooms && (
                        <span>{result.rooms} camere</span>
                      )}
                      {result.is_business && (
                        <Badge variant="outline" className="text-xs py-0 px-1 bg-amber-50 text-amber-700 border-amber-300">
                          Agent
                        </Badge>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => window.open(result.listing_url, '_blank')}
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        Vezi pe OLX
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => handleFetchPhone(result.external_id)}
                        disabled={fetchingPhone === result.external_id}
                      >
                        {fetchingPhone === result.external_id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : result.phone ? (
                          <>
                            <Phone className="w-3 h-3 mr-1 text-emerald-600" />
                            {result.phone}
                          </>
                        ) : (
                          <>
                            <Phone className="w-3 h-3 mr-1" />
                            Telefon
                          </>
                        )}
                      </Button>
                      
                      <Button
                        size="sm"
                        className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => handleImport(result.external_id)}
                        disabled={importingId === result.external_id}
                      >
                        {importingId === result.external_id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <Download className="w-3 h-3 mr-1" />
                            Import
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Parser pentru text copiat din OLX/Publi24
const parseClipboardText = (text) => {
  const result = {
    title: "",
    price: "",
    currency: "EUR",
    surface: "",
    rooms: "2",
    location: "",
    description: "",
    phone: ""
  };
  
  if (!text) return result;
  
  const lines = text.split('\n').map(l => l.trim()).filter(l => l);
  
  // Titlu - de obicei prima linie sau cea mai lungă din primele 3
  if (lines.length > 0) {
    const titleCandidates = lines.slice(0, 3);
    result.title = titleCandidates.reduce((a, b) => a.length > b.length ? a : b, "");
  }
  
  // Preț - caută pattern-uri de preț
  const pricePatterns = [
    /(\d[\d\s.,]*)\s*€/i,
    /(\d[\d\s.,]*)\s*EUR/i,
    /(\d[\d\s.,]*)\s*euro/i,
    /(\d[\d\s.,]*)\s*lei/i,
    /(\d[\d\s.,]*)\s*RON/i,
    /pret[:\s]*(\d[\d\s.,]*)/i,
    /price[:\s]*(\d[\d\s.,]*)/i
  ];
  
  for (const pattern of pricePatterns) {
    const match = text.match(pattern);
    if (match) {
      result.price = match[1].replace(/[\s.,]/g, '').replace(/,/g, '');
      if (text.toLowerCase().includes('lei') || text.toLowerCase().includes('ron')) {
        result.currency = "RON";
      }
      break;
    }
  }
  
  // Suprafață - caută mp, m², metri pătrați
  const surfacePatterns = [
    /(\d+)\s*m[²p2]/i,
    /(\d+)\s*mp/i,
    /(\d+)\s*metri/i,
    /suprafata[:\s]*(\d+)/i,
    /suprafață[:\s]*(\d+)/i,
    /surface[:\s]*(\d+)/i
  ];
  
  for (const pattern of surfacePatterns) {
    const match = text.match(pattern);
    if (match) {
      result.surface = match[1];
      break;
    }
  }
  
  // Camere
  const roomsPatterns = [
    /(\d+)\s*camer/i,
    /(\d+)\s*room/i,
    /apartament\s*(\d+)\s*camer/i,
    /(\d+)\s*cam\b/i
  ];
  
  for (const pattern of roomsPatterns) {
    const match = text.match(pattern);
    if (match) {
      result.rooms = match[1];
      break;
    }
  }
  
  // Telefon
  const phonePatterns = [
    /(\+?40\s*\d{3}\s*\d{3}\s*\d{3})/,
    /(07\d{2}\s*\d{3}\s*\d{3})/,
    /(\d{4}\s*\d{3}\s*\d{3})/
  ];
  
  for (const pattern of phonePatterns) {
    const match = text.match(pattern);
    if (match) {
      result.phone = match[1].replace(/\s/g, '');
      break;
    }
  }
  
  // Locație - caută orașe comune din România
  const cities = [
    "București", "Sector 1", "Sector 2", "Sector 3", "Sector 4", "Sector 5", "Sector 6",
    "Cluj-Napoca", "Cluj", "Timișoara", "Iași", "Constanța", "Craiova", "Brașov", 
    "Galați", "Ploiești", "Oradea", "Brăila", "Arad", "Pitești", "Sibiu",
    "Bacău", "Târgu Mureș", "Baia Mare", "Buzău", "Botoșani", "Satu Mare",
    "Râmnicu Vâlcea", "Drobeta", "Suceava", "Piatra Neamț", "Târgu Jiu",
    "Tulcea", "Focșani", "Bistrița", "Reșița", "Slatina", "Călărași"
  ];
  
  for (const city of cities) {
    if (text.toLowerCase().includes(city.toLowerCase())) {
      result.location = city;
      
      // Încearcă să găsească și zona/cartierul
      const zoneMatch = text.match(new RegExp(`${city}[,\\s]+(\\w+[\\w\\s-]*)`, 'i'));
      if (zoneMatch) {
        result.location = `${city}, ${zoneMatch[1].trim().substring(0, 30)}`;
      }
      break;
    }
  }
  
  // Descriere - restul textului (primele 500 caractere)
  if (lines.length > 1) {
    const descLines = lines.slice(1).filter(l => 
      !l.match(/^\d+\s*(€|EUR|lei|RON)/i) && 
      !l.match(/^(telefon|contact|pret|price)/i)
    );
    result.description = descLines.join(' ').substring(0, 500);
  }
  
  return result;
};

const CrawlerPanel = () => {
  const [url, setUrl] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [scraping, setScraping] = useState(false);
  const [searching, setSearching] = useState(false);
  const [importing, setImporting] = useState(false);
  const [scrapedData, setScrapedData] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [clipboardText, setClipboardText] = useState("");
  const [parsingClipboard, setParsingClipboard] = useState(false);
  
  // Manual listing form
  const [manualForm, setManualForm] = useState({
    title: "",
    price: "",
    currency: "EUR",
    surface: "",
    rooms: "2",
    location: "",
    description: "",
    source_url: "",
    phone: "",
    energy_certificate: ""
  });
  const [addingManual, setAddingManual] = useState(false);

  const handleImportFromClipboard = async () => {
    setParsingClipboard(true);
    
    try {
      // Citește din clipboard
      const text = await navigator.clipboard.readText();
      
      if (!text || text.trim().length < 10) {
        toast.error("Clipboard-ul este gol sau conține prea puțin text");
        setParsingClipboard(false);
        return;
      }
      
      setClipboardText(text);
      
      // Parsează textul
      const parsed = parseClipboardText(text);
      
      // Actualizează formularul
      setManualForm(prev => ({
        ...prev,
        title: parsed.title || prev.title,
        price: parsed.price || prev.price,
        currency: parsed.currency,
        surface: parsed.surface || prev.surface,
        rooms: parsed.rooms || prev.rooms,
        location: parsed.location || prev.location,
        description: parsed.description || prev.description,
        phone: parsed.phone || prev.phone
      }));
      
      // Afișează ce s-a găsit
      const found = [];
      if (parsed.title) found.push("titlu");
      if (parsed.price) found.push("preț");
      if (parsed.surface) found.push("suprafață");
      if (parsed.rooms !== "2") found.push("camere");
      if (parsed.location) found.push("locație");
      if (parsed.phone) found.push("telefon");
      
      if (found.length > 0) {
        toast.success(`Am extras: ${found.join(", ")}`);
      } else {
        toast.info("Nu am putut extrage date automat. Completează manual.");
      }
      
    } catch (error) {
      console.error("Clipboard error:", error);
      toast.error("Nu am acces la clipboard. Permite accesul în browser sau lipește manual textul.");
    } finally {
      setParsingClipboard(false);
    }
  };

  const handleParseTextarea = () => {
    if (!clipboardText.trim()) {
      toast.error("Lipește textul în câmpul de mai sus");
      return;
    }
    
    const parsed = parseClipboardText(clipboardText);
    
    setManualForm(prev => ({
      ...prev,
      title: parsed.title || prev.title,
      price: parsed.price || prev.price,
      currency: parsed.currency,
      surface: parsed.surface || prev.surface,
      rooms: parsed.rooms || prev.rooms,
      location: parsed.location || prev.location,
      description: parsed.description || prev.description,
      phone: parsed.phone || prev.phone
    }));
    
    const found = [];
    if (parsed.title) found.push("titlu");
    if (parsed.price) found.push("preț");
    if (parsed.surface) found.push("suprafață");
    if (parsed.rooms !== "2") found.push("camere");
    if (parsed.location) found.push("locație");
    if (parsed.phone) found.push("telefon");
    
    if (found.length > 0) {
      toast.success(`Am extras: ${found.join(", ")}`);
    } else {
      toast.info("Nu am putut extrage date. Verifică textul.");
    }
  };

  const handleScrape = async () => {
    if (!url.trim()) {
      toast.error("Introdu un URL");
      return;
    }

    setScraping(true);
    setScrapedData(null);
    
    try {
      const response = await axios.post(`${API}/crawler/scrape`, {
        url: url.trim(),
        source: "auto"
      });
      
      setScrapedData(response.data);
      toast.success("Date extrase cu succes!");
      
    } catch (error) {
      console.error("Scrape error:", error);
      toast.error(error.response?.data?.detail || "Eroare la extragerea datelor");
    } finally {
      setScraping(false);
    }
  };

  const handleImport = async () => {
    if (!url.trim()) {
      toast.error("Introdu un URL");
      return;
    }

    setImporting(true);
    
    try {
      const response = await axios.post(`${API}/crawler/import`, {
        url: url.trim(),
        source: "auto"
      });
      
      toast.success(response.data.message);
      setScrapedData(response.data.scraped_data);
      setUrl("");
      
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.detail || "Eroare la importare");
    } finally {
      setImporting(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error("Introdu un termen de căutare");
      return;
    }

    setSearching(true);
    setSearchResults([]);
    
    try {
      const response = await axios.get(`${API}/crawler/search/olx`, {
        params: { query: searchQuery.trim(), limit: 10 }
      });
      
      setSearchResults(response.data.results);
      
      if (response.data.results.length === 0) {
        toast.info("Nu am găsit rezultate");
      } else {
        toast.success(`${response.data.count} rezultate găsite`);
      }
      
    } catch (error) {
      console.error("Search error:", error);
      toast.error(error.response?.data?.detail || "Eroare la căutare");
    } finally {
      setSearching(false);
    }
  };

  const importFromSearch = async (resultUrl) => {
    setUrl(resultUrl);
    
    try {
      const response = await axios.post(`${API}/crawler/import`, {
        url: resultUrl,
        source: "auto"
      });
      
      toast.success(response.data.message);
      
      // Remove from search results
      setSearchResults(prev => prev.filter(r => r.url !== resultUrl));
      
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.detail || "Eroare la importare");
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    
    if (!manualForm.title || !manualForm.price || !manualForm.surface) {
      toast.error("Completează titlul, prețul și suprafața");
      return;
    }
    
    setAddingManual(true);
    
    try {
      const response = await axios.post(`${API}/crawler/manual`, {
        ...manualForm,
        price: parseFloat(manualForm.price),
        surface: parseFloat(manualForm.surface),
        rooms: parseInt(manualForm.rooms),
        energy_certificate: manualForm.energy_certificate || null
      });
      
      toast.success(response.data.message);
      
      // Reset form
      setManualForm({
        title: "",
        price: "",
        currency: "EUR",
        surface: "",
        rooms: "2",
        location: "",
        description: "",
        source_url: "",
        phone: "",
        energy_certificate: ""
      });
      
    } catch (error) {
      console.error("Manual add error:", error);
      toast.error(error.response?.data?.detail || "Eroare la adăugare");
    } finally {
      setAddingManual(false);
    }
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="api-search" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="api-search">🚀 Căutare API</TabsTrigger>
          <TabsTrigger value="import">Import URL</TabsTrigger>
          <TabsTrigger value="search">Căutare Clasică</TabsTrigger>
          <TabsTrigger value="manual">Adaugă Manual</TabsTrigger>
        </TabsList>

        {/* NEW: API-based Search (Most Reliable) */}
        <TabsContent value="api-search" className="mt-4 space-y-4">
          <OLXApiSearch />
        </TabsContent>

        <TabsContent value="import" className="mt-4 space-y-4">
          {/* Warning about limitations */}
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold mb-1">Limitări Crawler</p>
                <p>Site-urile mari (OLX, Imobiliare.ro) blochează scraping-ul automat. 
                   Dacă primești eroare, folosește <strong>Tab "Adaugă Manual"</strong> - 
                   copiezi textul anunțului și se completează automat!</p>
              </div>
            </div>
          </div>
          
          {/* URL Input */}
          <div className="space-y-2">
            <Label>URL anunț (OLX sau Publi24)</Label>
            <div className="flex gap-2">
              <Input
                data-testid="crawler-url-input"
                placeholder="https://www.olx.ro/d/oferta/..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="flex-1"
              />
              <Button
                onClick={handleScrape}
                disabled={scraping || !url.trim()}
                variant="outline"
              >
                {scraping ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </Button>
              <Button
                data-testid="import-listing-button"
                onClick={handleImport}
                disabled={importing || !url.trim()}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Import...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Importă
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-stone-500">
              Suportăm: OLX, Publi24, Homezz, Anuntul.ro, Imobiliare.ro, Storia.ro, Facebook
            </p>
          </div>
          
          {/* Supported Sources Info */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
            {[
              { name: "OLX", color: "bg-blue-100 text-blue-700" },
              { name: "Publi24", color: "bg-purple-100 text-purple-700" },
              { name: "Homezz", color: "bg-green-100 text-green-700" },
              { name: "Anuntul", color: "bg-orange-100 text-orange-700" },
              { name: "Imobiliare", color: "bg-red-100 text-red-700" },
              { name: "Storia", color: "bg-cyan-100 text-cyan-700" },
              { name: "Facebook", color: "bg-indigo-100 text-indigo-700" },
            ].map(source => (
              <Badge key={source.name} className={`${source.color} justify-center text-xs`}>
                {source.name}
              </Badge>
            ))}
          </div>

          {/* Scraped Data Preview */}
          {scrapedData && (
            <Card className="mt-4">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Date extrase</CardTitle>
                  <Badge variant="outline" className="uppercase">
                    {scrapedData.source}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-stone-500">Titlu</p>
                    <p className="font-medium">{scrapedData.title || "-"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-stone-500">Preț</p>
                    <p className="font-medium text-emerald-600">
                      {scrapedData.price ? `${scrapedData.price.toLocaleString()} ${scrapedData.currency}` : "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-stone-500">Suprafață</p>
                    <p className="font-medium">{scrapedData.surface ? `${scrapedData.surface} mp` : "-"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-stone-500">Camere</p>
                    <p className="font-medium">{scrapedData.rooms || "-"}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-stone-500">Locație</p>
                    <p className="font-medium">{scrapedData.location || "-"}</p>
                  </div>
                </div>
                
                {scrapedData.images?.length > 0 && (
                  <div>
                    <p className="text-xs text-stone-500 mb-2">Imagini ({scrapedData.images.length})</p>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {scrapedData.images.slice(0, 5).map((img, idx) => (
                        <img 
                          key={idx}
                          src={img}
                          alt={`Preview ${idx + 1}`}
                          className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {scrapedData.description && (
                  <div>
                    <p className="text-xs text-stone-500 mb-1">Descriere</p>
                    <p className="text-sm text-stone-600 line-clamp-3">{scrapedData.description}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="search" className="mt-4 space-y-4">
          {/* Search Input */}
          <div className="space-y-2">
            <Label>Caută pe OLX</Label>
            <div className="flex gap-2">
              <Input
                data-testid="crawler-search-input"
                placeholder="ex: apartament 2 camere bucuresti"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                className="flex-1"
              />
              <Button
                data-testid="crawler-search-button"
                onClick={handleSearch}
                disabled={searching || !searchQuery.trim()}
              >
                {searching ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Caută
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm text-stone-500">{searchResults.length} rezultate</p>
              
              {searchResults.map((result, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors"
                >
                  <div className="flex-1 min-w-0 mr-4">
                    <p className="font-medium text-sm truncate">{result.title}</p>
                    <a 
                      href={result.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-500 hover:underline flex items-center gap-1"
                    >
                      <Globe className="w-3 h-3" />
                      Vezi pe {result.source.toUpperCase()}
                    </a>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => importFromSearch(result.url)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Import
                  </Button>
                </div>
              ))}
            </div>
          )}

          {searchResults.length === 0 && searchQuery && !searching && (
            <div className="text-center py-8 text-stone-500">
              <AlertCircle className="w-10 h-10 mx-auto mb-2 text-stone-300" />
              <p>Niciun rezultat găsit</p>
              <p className="text-sm">Site-ul poate bloca requesturile automate. Folosește tab-ul "Adaugă Manual".</p>
            </div>
          )}
        </TabsContent>

        {/* Manual Add Tab */}
        <TabsContent value="manual" className="mt-4">
          {/* Clipboard Import Section */}
          <div className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Clipboard className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-stone-800">Import rapid din clipboard</h4>
                <p className="text-sm text-stone-600">
                  Copiază textul anunțului de pe OLX/Publi24 și apasă butonul pentru a completa automat formularul.
                </p>
              </div>
            </div>
            
            <div className="flex gap-2 mb-3">
              <Button
                type="button"
                data-testid="import-clipboard-button"
                onClick={handleImportFromClipboard}
                disabled={parsingClipboard}
                className="bg-amber-500 hover:bg-amber-600"
              >
                {parsingClipboard ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Se procesează...
                  </>
                ) : (
                  <>
                    <Clipboard className="w-4 h-4 mr-2" />
                    Importă de pe clipboard
                  </>
                )}
              </Button>
            </div>
            
            {/* Alternativ: Textarea pentru paste manual */}
            <details className="mt-2">
              <summary className="text-sm text-stone-500 cursor-pointer hover:text-stone-700">
                Nu funcționează? Lipește manual textul aici ↓
              </summary>
              <div className="mt-2 space-y-2">
                <Textarea
                  placeholder="Lipește aici textul copiat din anunțul OLX/Publi24..."
                  value={clipboardText}
                  onChange={(e) => setClipboardText(e.target.value)}
                  rows={4}
                  className="text-sm"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleParseTextarea}
                >
                  <Sparkles className="w-4 h-4 mr-1" />
                  Extrage datele
                </Button>
              </div>
            </details>
          </div>
          
          <form onSubmit={handleManualSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Titlu anunț *</Label>
                <Input
                  data-testid="manual-title-input"
                  placeholder="ex: Apartament 2 camere Unirii"
                  value={manualForm.title}
                  onChange={(e) => setManualForm({...manualForm, title: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label>Preț *</Label>
                <Input
                  type="number"
                  placeholder="ex: 85000"
                  value={manualForm.price}
                  onChange={(e) => setManualForm({...manualForm, price: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label>Monedă</Label>
                <select
                  className="w-full h-10 px-3 border rounded-md"
                  value={manualForm.currency}
                  onChange={(e) => setManualForm({...manualForm, currency: e.target.value})}
                >
                  <option value="EUR">EUR</option>
                  <option value="RON">RON</option>
                </select>
              </div>
              
              <div>
                <Label>Suprafață (mp) *</Label>
                <Input
                  type="number"
                  placeholder="ex: 65"
                  value={manualForm.surface}
                  onChange={(e) => setManualForm({...manualForm, surface: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label>Camere</Label>
                <select
                  className="w-full h-10 px-3 border rounded-md"
                  value={manualForm.rooms}
                  onChange={(e) => setManualForm({...manualForm, rooms: e.target.value})}
                >
                  {[1,2,3,4,5,6].map(n => (
                    <option key={n} value={n}>{n} camere</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label className="flex items-center gap-1">
                  <Zap className="w-3 h-3 text-amber-500" />
                  Clasa energetică
                </Label>
                <select
                  className="w-full h-10 px-3 border rounded-md"
                  value={manualForm.energy_certificate}
                  onChange={(e) => setManualForm({...manualForm, energy_certificate: e.target.value})}
                >
                  <option value="">Selectează clasa</option>
                  <option value="A">Clasa A (Verde)</option>
                  <option value="B">Clasa B (Verde)</option>
                  <option value="C">Clasa C</option>
                  <option value="D">Clasa D</option>
                  <option value="E">Clasa E</option>
                  <option value="F">Clasa F</option>
                  <option value="G">Clasa G</option>
                </select>
                <p className="text-xs text-stone-400 mt-1">
                  Clasa A beneficiază de reduceri la credite verzi
                </p>
              </div>
              
              <div className="col-span-2">
                <Label>Locație</Label>
                <Input
                  placeholder="ex: București, Sector 3, Unirii"
                  value={manualForm.location}
                  onChange={(e) => setManualForm({...manualForm, location: e.target.value})}
                />
              </div>
              
              <div className="col-span-2">
                <Label>URL sursă (OLX, Publi24, Facebook, etc.)</Label>
                <Input
                  placeholder="https://www.olx.ro/... sau facebook.com/marketplace/..."
                  value={manualForm.source_url}
                  onChange={(e) => setManualForm({...manualForm, source_url: e.target.value})}
                />
                <p className="text-xs text-stone-400 mt-1">
                  Suportăm: OLX, Publi24, Homezz, Anuntul.ro, Imobiliare.ro, Storia.ro, Facebook Marketplace
                </p>
              </div>
              
              <div className="col-span-2">
                <Label>Telefon proprietar</Label>
                <Input
                  placeholder="+40 7xx xxx xxx"
                  value={manualForm.phone}
                  onChange={(e) => setManualForm({...manualForm, phone: e.target.value})}
                />
              </div>
              
              <div className="col-span-2">
                <Label>Descriere</Label>
                <Textarea
                  placeholder="Descriere scurtă a proprietății..."
                  value={manualForm.description}
                  onChange={(e) => setManualForm({...manualForm, description: e.target.value})}
                  rows={3}
                />
              </div>
            </div>
            
            <Button 
              type="submit"
              data-testid="manual-add-button"
              disabled={addingManual}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              {addingManual ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Se adaugă...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Adaugă anunț
                </>
              )}
            </Button>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CrawlerPanel;
